<template>
  <div class="accordion" :style="elementStyle" v-on="$listeners">
    <slot />
  </div>
</template>

<script>
const DEFAULT_TRANSITION_TIME = 400

export default {
  name: 'Accordion',

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },

    transitionTime: {
      type: Number,
      default: DEFAULT_TRANSITION_TIME,
    },

    isInstant: {
      type: Boolean,
      default: false,
    },

    isHeightAuto: {
      type: Boolean,
      default: true,
    },

    /** When closed, use this height instead of 0 (number = px, or CSS length string). */
    collapsedHeight: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      height: 'auto',
      heightAnimationTimeout: null,
      isTransitioning: false,
    }
  },

  computed: {
    elementStyle() {
      return {
        height: this.height,
        transitionDuration: this.isInstant ? '' : `${this.transitionTime}ms`,
      }
    },

    closedHeight() {
      if (this.collapsedHeight == null || this.collapsedHeight === '') return '0px'
      return typeof this.collapsedHeight === 'number'
        ? `${this.collapsedHeight}px`
        : String(this.collapsedHeight)
    },
  },

  watch: {
    async isOpen() {
      if (!this.isOpen && this.isHeightAuto) {
        this.setHeightToScrollHeight()
        await this.$nextTick()
      }
      // Wait for slot content (e.g. expanded review text) before measuring.
      if (this.isOpen) await this.$nextTick()

      this.$emit('height-change', this.$el.scrollHeight * (this.isOpen ? 1 : -1))
      this.isTransitioning = this.isInstant ? false : true
      this.updateHeight()

      if (this.heightAnimationTimeout) clearTimeout(this.heightAnimationTimeout)
      this.heightAnimationTimeout = setTimeout(
        this.afterHeightChange,
        this.isInstant ? 0 : this.transitionTime
      )
    },

    collapsedHeight() {
      if (!this.isOpen && !this.isTransitioning) this.height = this.closedHeight
    },
  },

  mounted() {
    this.updateHeight()
  },

  methods: {
    updateHeight() {
      if (!this.isOpen) return (this.height = this.closedHeight)
      if (!this.isTransitioning && this.isHeightAuto) return (this.height = 'auto')
      this.setHeightToScrollHeight()
    },

    setHeightToScrollHeight() {
      this.height = this.isInstant
        ? 'auto'
        : this.$el?.scrollHeight
          ? `${this.$el.scrollHeight}px`
          : 'auto'
    },

    /**
     * Animate from the current rendered height to the current scrollHeight.
     * Use when content grows while the accordion is already open.
     */
    forceAnimateHeight() {
      if (!this.isOpen || this.isInstant) {
        this.updateHeight()
        return
      }

      this.animateToHeight(this.$el?.scrollHeight)
    },

    /**
     * Animate to an explicit pixel height (keeps a fixed height afterwards).
     * Useful for clipped layouts that should grow by a delta, not to full content.
     */
    animateToHeight(targetHeight) {
      if (targetHeight == null) return

      const fromHeight = this.$el?.offsetHeight
      const nextHeight =
        typeof targetHeight === 'number' ? `${targetHeight}px` : String(targetHeight)

      this.height = fromHeight ? `${fromHeight}px` : this.height
      this.isTransitioning = true

      if (this.heightAnimationTimeout) clearTimeout(this.heightAnimationTimeout)

      this.$nextTick(() => {
        // Force a layout flush so the browser registers the from-height before animating.
        void this.$el?.offsetHeight
        this.height = nextHeight
        this.heightAnimationTimeout = setTimeout(() => {
          this.$emit('height-change-end')
          this.isTransitioning = false
        }, this.isInstant ? 0 : this.transitionTime)
      })
    },

    afterHeightChange() {
      if (this.isOpen && this.isHeightAuto) this.height = 'auto'
      this.$emit('height-change-end')
      this.isTransitioning = false
    },
  },
}
</script>

<style lang="scss" scoped>
.accordion {
  position: relative;
  overflow: hidden;
  transition-property: height;
  transition-timing-function: ease-out;
  will-change: height;
}
</style>
